import { useState, useEffect, useCallback, useRef } from "react";
import type { UserProfile } from "@/app/profile/types";
import { getProfile, updateProfile } from "@/lib/api/profile";
import type { User } from "@supabase/supabase-js";
import { useAuthContext } from "@/lib/context/AuthContext";

interface UseProfileResult {
  user: User | null;
  profile: UserProfile | null;
  loading: boolean;
  error: Error | null;
  saveProfile: (updates: Partial<UserProfile>) => Promise<UserProfile>;
}

export function useProfile(): UseProfileResult {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const loadingRef = useRef(false);
  const { user, loading: authLoading } = useAuthContext();

  const loadProfile = useCallback(async (currentUser: User) => {
    if (loadingRef.current) return;
    loadingRef.current = true;

    try {
      console.log("Fetching profile from API");
      const profileData = await getProfile();
      if (profileData) {
        setProfile(profileData);
      } else {
        const newProfile: Partial<UserProfile> = {
          user_id: currentUser.id,
          email: currentUser.email ?? undefined,
          full_name: currentUser.user_metadata?.full_name ?? undefined,
          avatar_url: currentUser.user_metadata?.avatar_url ?? undefined,
          travel_style: [],
          dietary_restrictions: [],
          notification_preferences: {
            push: true,
            deals: false,
            email: true,
            trip_updates: false,
          },
        };
        const created = await updateProfile(newProfile);
        setProfile(created);
      }
    } catch (err: any) {
      console.error("Profile loading error:", err);
      setError(err);
    } finally {
      loadingRef.current = false;
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    if (authLoading) {
      if (mounted) setLoading(true);
      return;
    }
    if (!user) {
      if (mounted) {
        setProfile(null);
        setLoading(false);
      }
      return;
    }
    (async () => {
      try {
        if (mounted) setLoading(true);
        await loadProfile(user);
      } catch {
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [authLoading, user, loadProfile]);

  const saveProfile = async (updates: Partial<UserProfile>) => {
    setLoading(true);
    try {
      console.log("Saving profile");
      const updated = await updateProfile(updates);
      if (updated) {
        setProfile(updated);
      }
      return updated;
    } catch (err: any) {
      if (err.message.includes("duplicate key value")) {
        const updated = await updateProfile(updates);
        setProfile(updated);
        return updated;
      }
      console.error("Profile save error:", err);
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { user, profile, loading, error, saveProfile };
}
